import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../axios/axios-instance";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import LabeledInput from "../../../components/common/fields/LabeledInput";
import { pathAPI } from "../../../utils/pathApi";
import { CancelReason, CancelReasonFormData } from "./cancelReason.types";

export interface CancelReasonDialogProps {
  open: boolean;
  /** null = Add mode, otherwise Edit mode prefilled from this row */
  reason: CancelReason | null;
  /** Language id used only when creating a new row */
  languageId: number;
  onClose: () => void;
  onSaved: (successMessage: string) => void;
  onError: (errorMessage: string) => void;
}

const EMPTY_FORM: CancelReasonFormData = { message: "", code: "" };

const CancelReasonDialog = ({
  open,
  reason,
  languageId,
  onClose,
  onSaved,
  onError,
}: CancelReasonDialogProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CancelReasonFormData>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (reason) {
      setFormData({
        message: reason.message || "",
        code: reason.code || "",
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [reason, open]);

  const handleChange = (field: keyof CancelReasonFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.code.trim() || !formData.message.trim()) {
      onError(t("cancel_reason.dialog.required"));
      return;
    }
    const message = formData.message.trim();

    try {
      setSubmitting(true);
      const code = formData.code.trim().toUpperCase() || undefined;
      if (reason) {
        await axiosInstance.put(`${pathAPI.cancelReason}/${reason.id}`, {
          message,
          code,
        });
        onSaved(t("cancel_reason.dialog.updated_ok"));
      } else {
        // Server also AI-translates the new row into every other active language.
        await axiosInstance.post(pathAPI.cancelReason, {
          message,
          code,
          language: languageId,
        });
        onSaved(t("cancel_reason.dialog.added_ok"));
      }
      onClose();
    } catch (error) {
      console.error("Error saving cancel reason:", error);
      onError(t("cancel_reason.dialog.save_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {reason ? t("cancel_reason.dialog.edit_title") : t("cancel_reason.dialog.add_title")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <LabeledInput
            label={t("cancel_reason.dialog.code")}
            value={formData.code}
            onChange={(e) => handleChange("code", e.target.value)}
            placeholder="PATCH_TEST_REQUIRED"
            inputProps={{ maxLength: 64 }}
          />
          <LabeledInput
            label={t("cancel_reason.dialog.message")}
            required
            multiline
            minRows={3}
            value={formData.message}
            onChange={(e) => handleChange("message", e.target.value)}
            placeholder={t("cancel_reason.dialog.message_ph")}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <CancelButton onClick={onClose} disabled={submitting}>
          {t("btn.cancel")}
        </CancelButton>
        <ActionButton onClick={handleSubmit} disabled={submitting}>
          {submitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              {t("cancel_reason.dialog.saving")}
            </>
          ) : (
            t("btn.save")
          )}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default CancelReasonDialog;
